import { analytics } from './analytics.js';
import { createPlayBadge, createSocialMark } from './social-marks.js';
import { sealShowsShield } from './resolve-trust.js';
import { trustCardMarks, trustCardShapes } from './constants.js';
import { xMarkIconSvg } from './leadwin-agent.js';

/**
 * The fixed corner the trust surface stands in, and the control that takes it away. A wrapper rather
 * than a close button hung on the card itself, because the card IS a button and a control nested inside
 * it would be a click target inside a click target. It is also the room the opt-out offer moves into
 * once the card is gone, which is the whole reason the wrapper outlives what it wrapped.
 * The close control is left off entirely without a label: it needs an accessible name, and an offer
 * nobody localized is one a visitor should not be shown (see resolveTrustLabels).
 * @param {{side: string, theme: string, trustLabels: object}} options - Normalized rendering options
 * @param {Function} onDismiss - Called when the visitor closes the surface
 * @returns {HTMLDivElement}
 */
function createTrustCorner(options, onDismiss) {
    const corner = document.createElement('div');
    corner.className = 'lw-trust-corner';
    corner.dataset.theme = options.theme;
    // Above the launcher, where the bubbles live. The bottom offset is re-measured whenever the nudge
    // stack changes (positionTrustCards): campaigns and bubbles keep the corner nearest the launcher,
    // the cards move up out of their way.
    // Placement stays inline here for the reason the nudge stack's does: only the loader pins anything
    // to the corner of a page it does not own. Everything a visitor sees lives in the stylesheet.
    corner.style.cssText =
        'position:fixed;' + options.side + ':18px;bottom:78px;z-index:2147483645;'
        + 'align-items:' + (options.side === 'left' ? 'flex-start' : 'flex-end') + ';';

    if (options.trustLabels.close) {
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'lw-trust-close';
        close.setAttribute('aria-label', options.trustLabels.close);
        close.innerHTML = xMarkIconSvg;
        close.addEventListener('click', onDismiss);
        corner.appendChild(close);
    }

    document.body.appendChild(corner);
    return corner;
}

/**
 * The rotating trust cards ABOVE the launcher — the whole of the trust surface. One card at a time,
 * swapped on a timer: a corner of somebody else's website has room for one thing, and a visitor reads
 * one anyway. Three shapes share that card — a customer quote, a project tile and an earned seal — and
 * the deck decides which is due; the heading above changes with it.
 * A button, not a link: tapping it opens THAT review, THAT project or the provider's profile inside the
 * widget, where each is read in full, instead of sending the visitor off the website they are on.
 * Everything visible comes from the payload as text (the cover as a URL the server sized); the look
 * lives in leadwin-nudge.css so the manage preview wears the identical card.
 * @param {{side: string, theme: string, trustCards: object}} options - Normalized rendering options
 * @param {Function} onOpen - Called with the shown card when it is tapped
 * @param {Function} onDismiss - Called when the visitor closes the corner
 * @returns {{el: HTMLButtonElement, corner: HTMLDivElement, show: Function, shownKind: Function}}
 */
export function createTrustBoard(options, onOpen, onDismiss) {
    const deck = options.trustCards;
    const corner = createTrustCorner(options, onDismiss);
    const board = document.createElement('button');
    board.id = 'feedbax-trust-cards';
    board.className = 'lw-trust-cards';
    board.type = 'button';
    board.dataset.theme = options.theme;

    // Names what is on the card right now ("Kundenstimmen", "Referenzen", "Auszeichnungen"). Localized
    // server-side, or
    // absent from an older payload — the card then stands on its own content rather than on an English
    // line nobody asked for, so the element is hidden instead of standing there empty.
    const heading = document.createElement('span');
    heading.className = 'lw-trust-cards-head';
    board.appendChild(heading);

    // The seal's mark, in the board's own top-right corner at the height of the heading. It hangs on
    // the BOARD rather than inside the seal card on purpose: the swapped shapes replay a transform
    // animation, and an animated transform makes the card its own containing block — the mark would
    // anchor to the card's corner instead of the board's, which is halfway down the card.
    const mark = document.createElement('span');
    mark.className = 'lw-trust-award-mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.style.display = 'none';
    board.appendChild(mark);

    // One element per SHAPE, built once and swapped by display: the board rebuilds nothing on a
    // rotation, and the manage preview renders the very same markup from the very same stylesheet.
    // Fewer shapes than kinds — the three seal forms look alike on purpose, and only the line under the
    // name and the mark in the corner tell a platform verdict from a line the provider typed.
    const shapes = {
        review: createTrustQuoteCard(),
        project: createTrustProjectCard(),
        social: createTrustSocialCard(),
        seal: createTrustSealCard(),
        ratingseal: createTrustRatingSealCard()
    };

    for (const kind in shapes) {
        board.appendChild(shapes[kind].el);
    }

    /** The card the visitor is looking at, handed to the click handler below. */
    let shown = null;

    /**
     * Puts one card of the deck on the board and replays its entry animation. The class is removed, the
     * layout is read once to flush it, then it goes back on — without that reflow the browser coalesces
     * both changes and no second card ever animates.
     * @param {number} index - Position in the deck, wrapped by the caller
     */
    function show(index) {
        const item = deck.items[index];
        if (!item) {
            return;
        }

        shown = item;

        // Reported on every deal; the beacon keeps the first one per FORM and pageview. Here rather
        // than at the board's creation, because the corner rotates through several forms and each of
        // them is a separate answer to "does this kind of proof work".
        analytics.onTrustImpression(item.kind);

        heading.textContent = item.heading || '';
        heading.style.display = item.heading ? '' : 'none';
        // Centred over the shield, like everything printed on it; left-bound over every other card.
        heading.classList.toggle('lw-trust-centered', sealShowsShield(item));
        // What stands on the card, for the stylesheet: only the seal's heading has to keep clear of the
        // corner the trophy hangs in, and only the seal brings that trophy out at all.
        board.dataset.kind = item.kind;

        // Each seal form wears its own mark: the platform's per-discipline seals a trophy, the
        // provider's own awards a medal, the "Empfehlungssiegel" a star. The rating card wears none —
        // its stars are the content, and a second one in the corner would say it twice. Neither does a
        // seal drawn as the badge shield: the shield brings its own emblem.
        const glyph = sealShowsShield(item) ? '' : trustCardMarks[item.kind] || '';
        mark.textContent = glyph;
        mark.style.display = glyph ? '' : 'none';

        // The accessible name follows the card: an empty one would be worse than none, so an older
        // payload without headings leaves the button to its own text.
        if (item.heading) {
            board.setAttribute('aria-label', item.heading);
        } else {
            board.removeAttribute('aria-label');
        }

        const card = shapes[trustCardShapes[item.kind]] || shapes.review;
        for (const kind in shapes) {
            if (shapes[kind] !== card) {
                shapes[kind].el.style.display = 'none';
            }
        }

        card.el.style.display = '';
        card.el.classList.remove('lw-trust-card-enter');
        void card.el.offsetWidth;
        card.fill(item);
        card.el.classList.add('lw-trust-card-enter');
    }

    // The card the visitor is looking at right now. The tap has to open THAT one, not the one the
    // rotation started on, so the handler reads it here rather than closing over an index.
    board.addEventListener('click', function () {
        if (shown) {
            // The counted click belongs to the FORM that was on the board, not to the review or the
            // seal on it: which form earns taps is the choice the manage card makes, and "which quote"
            // is a question nobody acts on.
            analytics.onTrustClick(shown.kind);
            onOpen(shown);
        }
    });

    show(0);
    corner.appendChild(board);
    return {
        el: board,
        /** The wrapper the board stands in — what the loader positions, moves and takes away. */
        corner: corner,
        show: show,
        /** The form standing on the board right now, for the route hook that re-counts it. */
        shownKind: function () {
            return shown ? shown.kind : '';
        }
    };
}

/**
 * The quote shape of the trust card: stars and platform on one line, the sentences, who said them.
 * @returns {{el: HTMLElement, fill: Function}}
 */
function createTrustQuoteCard() {
    const card = document.createElement('span');
    card.className = 'lw-trust-review';

    // The rating and where it was given, on one line above the quote.
    const head = document.createElement('span');
    head.className = 'lw-trust-review-head';
    card.appendChild(head);

    // Three spans rather than one string: the earned stars, the half one and the missing ones carry
    // different colours, and a four-and-a-half-star review has to be readable as one. Decorative on
    // purpose — a screen reader gets the heading, the quote and its author, which is the content; five
    // repeated glyphs are not.
    const stars = document.createElement('span');
    stars.className = 'lw-trust-review-stars';
    stars.setAttribute('aria-hidden', 'true');
    head.appendChild(stars);

    const starsOn = document.createElement('span');
    starsOn.className = 'lw-trust-review-stars-on';
    stars.appendChild(starsOn);

    const starsPart = document.createElement('span');
    starsPart.className = 'lw-trust-review-stars-part';
    starsPart.textContent = '★';
    stars.appendChild(starsPart);

    const starsOff = document.createElement('span');
    starsOff.className = 'lw-trust-review-stars-off';
    stars.appendChild(starsOff);

    // Where the quote was written. NOT decorative and therefore outside the stars: an imported quote
    // standing unmarked on a Feedbax card would read as a Feedbax one. Empty for the profile's own
    // reviews; the separator dot comes from the stylesheet, so an empty element leaves nothing behind.
    const source = document.createElement('span');
    source.className = 'lw-trust-review-source';
    head.appendChild(source);

    const text = document.createElement('span');
    text.className = 'lw-trust-review-quote';
    card.appendChild(text);

    const author = document.createElement('span');
    author.className = 'lw-trust-review-author';
    card.appendChild(author);

    /**
     * Draws one quote on the card.
     * @param {{review: object}} item - The deck entry to draw
     */
    function fill(item) {
        const review = item.review;
        const parts = starParts(review.rating);
        starsOn.textContent = '★'.repeat(parts.full);
        starsPart.style.display = parts.partial ? '' : 'none';
        starsPart.style.setProperty('--lw-trust-star-part', parts.partial + '%');
        starsOff.textContent = '☆'.repeat(parts.empty);
        source.textContent = review.source || '';
        text.textContent = review.quote;
        author.textContent = review.company ? review.author + ' · ' + review.company : review.author;
    }

    return { el: card, fill: fill };
}

/**
 * The card of one post or video the provider published, dressed as the network that published it.
 * It used to wear the project tile's clothes — a picture, a line, a grey word underneath — and read as a
 * news teaser from nowhere: the grey word "LinkedIn" is not a LinkedIn post, and a still with no play
 * badge is not a video. So the card now carries the three things every one of these platforms leads with
 * and a visitor recognises before reading anything: the face of whoever published it, that platform's own
 * mark in its own colour, and, over a video's still, the play badge its player uses.
 * The picture is optional, unlike on a project tile. A written post is often words alone, and dropping
 * those would quietly turn "what we publish" into "what we publish with pictures".
 * @returns {{el: HTMLSpanElement, fill: Function}}
 */
function createTrustSocialCard() {
    const card = document.createElement('span');
    card.className = 'lw-trust-social';
    card.style.display = 'none'; // The board shows whichever shape the first deck entry needs.

    // Who published it. The face first, then the name, then the network's own mark at the far end —
    // the row every one of these platforms puts above a post, in the order they all put it in.
    const head = document.createElement('span');
    head.className = 'lw-trust-social-head';
    card.appendChild(head);

    const avatar = document.createElement('img');
    avatar.className = 'lw-trust-social-avatar';
    avatar.alt = '';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.loading = 'lazy';
    avatar.decoding = 'async';
    head.appendChild(avatar);

    const author = document.createElement('span');
    author.className = 'lw-trust-social-author';
    head.appendChild(author);

    const mark = createSocialMark();
    head.appendChild(mark.el);

    // The picture, and over it the play badge a video still needs. Both live in one positioned box so
    // the badge can sit on the picture without being able to escape a card that has none.
    const media = document.createElement('span');
    media.className = 'lw-trust-social-media';
    card.appendChild(media);

    const image = document.createElement('img');
    image.className = 'lw-trust-social-image';
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);

    const play = createPlayBadge();
    media.appendChild(play);

    const title = document.createElement('span');
    title.className = 'lw-trust-social-title';
    card.appendChild(title);

    /**
     * Draws one post on the card.
     * @param {{social: object}} item - The deck entry to draw
     */
    function fill(item) {
        const post = item.social;

        // The whole card is dressed off this one attribute: the mark's colour, the play badge's colour,
        // the crop the picture gets. A slug the stylesheet has no rule for simply gets the neutral set.
        card.dataset.network = post.slug || '';
        mark.set(post.slug);

        // Removed from the layout rather than left blank: an empty img keeps its box, and a grey circle
        // where a face belongs reads as a picture that failed to load.
        avatar.style.display = post.authorPhotoUrl ? '' : 'none';
        avatar.src = post.authorPhotoUrl || '';

        // The network's name stands in where the post named nobody, so the row is never empty: a head
        // row with a mark and no words looks like a heading that lost its text.
        author.textContent = post.authorName || post.network || '';

        media.style.display = post.imageUrl ? '' : 'none';
        image.src = post.imageUrl || '';

        // Only over a real still. A play badge on a card with no picture would be a button floating in
        // the words, and a video whose still never arrived is better shown as the words alone.
        play.style.display = post.isVideo && post.imageUrl ? '' : 'none';

        title.textContent = post.title;
    }

    return { el: card, fill: fill };
}

/**
 * The project shape of the trust card: the cover, the title, the client. The image carries no alt text —
 * the title right under it is the same information, and a screen reader that read both would hear the
 * project named twice.
 * @returns {{el: HTMLElement, fill: Function}}
 */
function createTrustProjectCard() {
    const card = document.createElement('span');
    card.className = 'lw-trust-project';
    card.style.display = 'none'; // The board shows whichever shape the first deck entry needs.

    const image = document.createElement('img');
    image.className = 'lw-trust-project-image';
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.loading = 'lazy';
    image.decoding = 'async';
    card.appendChild(image);

    const title = document.createElement('span');
    title.className = 'lw-trust-project-title';
    card.appendChild(title);

    // Who the project was done for. Empty where the case study names nobody, and the tile then stands on
    // its picture and its title.
    const client = document.createElement('span');
    client.className = 'lw-trust-project-client';
    card.appendChild(client);

    /**
     * Draws one project on the card.
     * @param {{project: object}} item - The deck entry to draw
     */
    function fill(item) {
        const project = item.project;
        image.src = project.imageUrl;
        title.textContent = project.title;
        client.textContent = project.client || '';
    }

    return { el: card, fill: fill };
}

/**
 * The seal shape of the trust card, shared by all three seal forms: the line — an earned "Top-Anbieter
 * für …", an award the provider wrote themselves, or the "Empfehlungssiegel" — and who stands behind
 * it. Its mark is not here but on the board (see createTrustBoard), which is the corner it hangs in —
 * and it is decorative either way: the seal's own name says what it is, and a reader that announced a
 * trophy first would hear the card twice. The line under the name is NOT decorative: it is the whole of
 * what separates the platform's verdict from a badge the provider drew themselves, and on a stranger's
 * website that has to be readable without tapping anything.
 * @returns {{el: HTMLElement, fill: Function}}
 */
function createTrustSealCard() {
    const card = document.createElement('span');
    card.className = 'lw-trust-award';
    card.style.display = 'none'; // The board shows whichever shape the first deck entry needs.

    const name = document.createElement('span');
    name.className = 'lw-trust-award-name';
    card.appendChild(name);

    // The earned seal's other face: the badge shield the legacy app renders, with "Top-Anbieter", the
    // category in gold and the year as real text over the artwork — real, so a screen reader gets the
    // same badge a sighted visitor does. Hidden while the card shows a text seal; only an earned seal
    // whose category and seal word both arrived swaps it in (see sealShowsShield).
    const shield = document.createElement('span');
    shield.className = 'lw-trust-shield';
    shield.style.display = 'none';
    card.appendChild(shield);

    const shieldText = document.createElement('span');
    shieldText.className = 'lw-trust-shield-text';
    shield.appendChild(shieldText);

    const shieldTop = document.createElement('span');
    shieldTop.className = 'lw-trust-shield-top';
    shieldText.appendChild(shieldTop);

    const shieldCategory = document.createElement('span');
    shieldCategory.className = 'lw-trust-shield-category';
    shieldText.appendChild(shieldCategory);

    const shieldYear = document.createElement('span');
    shieldYear.className = 'lw-trust-shield-year';
    shieldYear.textContent = String(new Date().getFullYear());
    shieldText.appendChild(shieldYear);

    /**
     * Draws one seal on the card, as a shield or as the text line. Two shield faces share the element:
     * the earned seal writes its lines over the artwork, the "Empfehlungssiegel" wears a per-language
     * artwork with the lettering baked in and writes nothing.
     * @param {{kind: string, seal: {name: string, category: string,
     *     sealWord: string, badgeLang: string}}} item - The deck entry to draw
     */
    function fill(item) {
        const asShield = sealShowsShield(item);
        const asTopRated = asShield && item.kind === 'toprated';

        name.textContent = asShield ? '' : item.seal.name;
        name.style.display = asShield ? 'none' : '';
        shieldTop.textContent = asShield && !asTopRated ? item.seal.sealWord : '';
        shieldCategory.textContent = asShield && !asTopRated ? item.seal.category : '';
        shieldText.style.display = asTopRated ? 'none' : '';
        shield.classList.toggle('lw-trust-shield-toprated', asTopRated);
        shield.dataset.lang = asTopRated ? item.seal.badgeLang : '';
        shield.style.display = asShield ? '' : 'none';
    }

    return { el: card, fill: fill };
}

/**
 * The "Bewertungssiegel" shape: the average large enough to be read at a glance, its stars beside it,
 * what it rests on underneath and the line that says whose aggregate it is. The same facts the compact
 * pill carries — the pill is what stands in the corner while no form is ticked, this is a form, and a
 * contractor who leads with quotes may still want the number to come past every few seconds.
 * @returns {{el: HTMLElement, fill: Function}}
 */
function createTrustRatingSealCard() {
    const card = document.createElement('span');
    card.className = 'lw-trust-rating';
    card.style.display = 'none';

    // The number and its stars on one line: a rating read without its scale is a rating misread.
    const head = document.createElement('span');
    head.className = 'lw-trust-rating-head';
    card.appendChild(head);

    const value = document.createElement('span');
    value.className = 'lw-trust-rating-value';
    head.appendChild(value);

    // Decorative, exactly like the quote card's: the number beside them is the content, and five
    // repeated glyphs announced after it would be the same fact told twice.
    const stars = document.createElement('span');
    stars.className = 'lw-trust-review-stars';
    stars.setAttribute('aria-hidden', 'true');
    head.appendChild(stars);

    const starsOn = document.createElement('span');
    starsOn.className = 'lw-trust-review-stars-on';
    stars.appendChild(starsOn);

    const starsPart = document.createElement('span');
    starsPart.className = 'lw-trust-review-stars-part';
    starsPart.textContent = '★';
    stars.appendChild(starsPart);

    const starsOff = document.createElement('span');
    starsOff.className = 'lw-trust-review-stars-off';
    stars.appendChild(starsOff);

    const count = document.createElement('span');
    count.className = 'lw-trust-rating-count';
    card.appendChild(count);

    // The rated criteria as bars under the count; stays empty (and takes no room) without any.
    const criteria = document.createElement('span');
    criteria.className = 'lw-trust-rating-criteria';
    card.appendChild(criteria);

    /**
     * Draws the rating on the card. The criteria rows are rebuilt per fill rather than kept as
     * elements: unlike the card's fixed lines, their number differs per widget.
     * @param {{ratingSeal: object}} item - The deck entry to draw
     */
    function fill(item) {
        const seal = item.ratingSeal;
        const parts = starParts(seal.rating);
        value.textContent = seal.ratingText;
        starsOn.textContent = '★'.repeat(parts.full);
        starsPart.style.display = parts.partial ? '' : 'none';
        starsPart.style.setProperty('--lw-trust-star-part', parts.partial + '%');
        starsOff.textContent = '☆'.repeat(parts.empty);
        count.textContent = seal.countText || '';

        criteria.textContent = '';
        const rows = seal.criteria || [];
        criteria.style.display = rows.length ? '' : 'none';
        for (const row of rows) {
            criteria.appendChild(createTrustCriterionRow(row));
        }
    }

    return { el: card, fill: fill };
}

/**
 * One criteria bar of the rating card: the label, the track with its fill, the average beside it. The
 * bar is decorative (aria-hidden) — label and number are the same fact as text.
 * @param {{label: string, value: number, valueText: string}} row - The resolved criterion
 * @returns {HTMLElement}
 */
function createTrustCriterionRow(row) {
    const el = document.createElement('span');
    el.className = 'lw-trust-criterion';

    const label = document.createElement('span');
    label.className = 'lw-trust-criterion-label';
    label.textContent = row.label;
    el.appendChild(label);

    const bar = document.createElement('span');
    bar.className = 'lw-trust-criterion-bar';
    bar.setAttribute('aria-hidden', 'true');
    el.appendChild(bar);

    const fill = document.createElement('span');
    fill.className = 'lw-trust-criterion-fill';
    fill.style.width = Math.max(0, Math.min(100, (row.value / 5) * 100)) + '%';
    bar.appendChild(fill);

    const valueText = document.createElement('span');
    valueText.className = 'lw-trust-criterion-value';
    valueText.textContent = row.valueText || '';
    el.appendChild(valueText);

    return el;
}

/**
 * How a rating fills a row of five stars: the ones earned in full, the share of the next one, and the
 * outlines left over. Glyphs rather than markup: this renders inside websites we do not control, where an
 * SVG would be one more thing their CSS can reach into — the partial star is one more glyph, clipped to
 * its share by the stylesheet.
 * Filled by width rather than rounded to whole or half stars, and the identical rule lives in C# as
 * StarFill for the app's own star rows and the dashboard preview: a 4,7 must not read as a 5 on one
 * surface and a 4,7 on the next.
 * @param {number} rating - Overall rating, 0 to 5
 * @returns {{full: number, partial: number, empty: number}} partial is 0-100, zero when none is drawn
 */
function starParts(rating) {
    const clamped = Math.max(0, Math.min(5, rating));
    let full = 0;
    let partial = 0;

    for (let star = 0; star < 5; star++) {
        // What THIS star gets of the rating: everything above the stars before it, capped at its own
        // whole. Star four of a 4,7 therefore takes 0,7 and the fifth nothing.
        const percent = Math.round(Math.max(0, Math.min(1, clamped - star)) * 100);

        if (percent === 100) {
            full++;
        } else if (percent > 0) {
            partial = percent;
        }
    }

    return { full: full, partial: partial, empty: 5 - full - (partial > 0 ? 1 : 0) };
}
